import { useMemo } from "react";

import type { ExtraProfileField } from "@jti/fe-common";
import type { FormTarget } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { useGetOrderAttributesQuery } from "@jti/store/slices/magentoApiSlice";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";

import { FISCAL_CODE_ATTRIBUTE_KEY } from "../../const";
import { selectVisibleAttributes } from "../../utils";
import { normalizeProfileExtraAttributes } from "../../utils/normalizeProfileExtraAttributes";

export const useVisibleOrderAttributes = (target: FormTarget) => {
  const orderAttributesQuery = useGetOrderAttributesQuery();
  const profileQuery = useGetProfileQuery();
  const checkoutData = useSelector((state) => state.checkoutV2.data);

  const normalizedExtraAttributes = useMemo(
    () => normalizeProfileExtraAttributes(profileQuery.data?.extra || {}),
    [profileQuery.data?.extra]
  );

  const savedFiscalCode = checkoutData?.orderAttributes?.[FISCAL_CODE_ATTRIBUTE_KEY];

  const excludedAttributes = useMemo((): ExtraProfileField => {
    const hasSavedFiscalCode = typeof savedFiscalCode === "string" && savedFiscalCode.trim().length > 0;

    if (!hasSavedFiscalCode) return normalizedExtraAttributes;

    return { ...normalizedExtraAttributes, [FISCAL_CODE_ATTRIBUTE_KEY]: savedFiscalCode };
  }, [normalizedExtraAttributes, savedFiscalCode]);

  const visibleAttributes = useMemo(
    () => selectVisibleAttributes(orderAttributesQuery.data, excludedAttributes, target),
    [orderAttributesQuery.data, excludedAttributes, target]
  );

  const isLoading = orderAttributesQuery.isLoading || profileQuery.isLoading;
  const isError = orderAttributesQuery.isError || profileQuery.isError;

  return {
    attributes: visibleAttributes,
    isLoading,
    isError,
    isEmpty: !isLoading && visibleAttributes.length === 0,
  };
};
